import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  alpha,
  useTheme as useMuiTheme,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Router as RouterIcon,
  ArrowForward as ArrowForwardIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as OpenIcon,
  LocalShipping as DispatchedIcon,
  LocationOn as OnSiteIcon,
} from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { risksData, getRiskStats } from "../risks/data/risksData";
import { getSightingStats } from "../sightings/data/sightingsData";
import { workOrdersData, getWorkOrderStats } from "../workorders/data/workOrdersData";
import { generateNodes } from "../geospatial/data/pipelineData";
import { ROUTES } from "../../shared/utils/constants";
import { formatDistanceToNow } from "date-fns";

// ── Summary Card ──────────────────────────────────────────────────────────────

interface SummaryCardProps {
  title: string;
  total: number;
  icon: React.ReactNode;
  color: string;
  subtitle: string;
  onClick: () => void;
  chips?: { label: string; color: "error" | "warning" | "info" | "success" | "default" }[];
}

const SummaryCard = ({ title, total, icon, color, subtitle, onClick, chips }: SummaryCardProps) => {
  const theme = useMuiTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 220,
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: color,
          boxShadow: `0 0 0 1px ${alpha(color, 0.3)}`,
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={600}>
              {total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.12),
              color: color,
              width: 44,
              height: 44,
            }}
          >
            {icon}
          </Avatar>
        </Stack>
        {chips && chips.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            {chips.map((chip) => (
              <Chip key={chip.label} label={chip.label} size="small" color={chip.color} variant="outlined" />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// ── Section Header ────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  title: string;
  action?: { label: string; onClick: () => void };
}

const SectionHeader = ({ title, action }: SectionHeaderProps) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    {action && (
      <Chip
        label={action.label}
        size="small"
        onClick={action.onClick}
        onDelete={action.onClick}
        deleteIcon={<ArrowForwardIcon fontSize="small" />}
        variant="outlined"
        color="primary"
        sx={{ cursor: "pointer" }}
      />
    )}
  </Stack>
);

// ── Work Order Pipeline ───────────────────────────────────────────────────────

const WorkOrderPipeline = ({ stats }: { stats: ReturnType<typeof getWorkOrderStats> }) => {
  const theme = useMuiTheme();
  const activeTotal = stats.open + stats.dispatched + stats.techAware + stats.onSite;

  const stages = [
    { label: "Open", value: stats.open, color: theme.palette.error.main, icon: <OpenIcon fontSize="small" /> },
    { label: "Dispatched", value: stats.dispatched, color: theme.palette.warning.main, icon: <DispatchedIcon fontSize="small" /> },
    { label: "Tech Aware", value: stats.techAware, color: theme.palette.info.main, icon: <ScheduleIcon fontSize="small" /> },
    { label: "On Site", value: stats.onSite, color: theme.palette.info.dark, icon: <OnSiteIcon fontSize="small" /> },
    { label: "Completed", value: stats.completed, color: theme.palette.success.main, icon: <CheckCircleIcon fontSize="small" /> },
  ];

  return (
    <Box>
      {stages.map((stage) => (
        <Box key={stage.label} sx={{ mb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ color: stage.color }}>{stage.icon}</Box>
              <Typography variant="body2">{stage.label}</Typography>
            </Stack>
            <Typography variant="body2" fontWeight={600}>
              {stage.value}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={activeTotal > 0 ? (stage.value / activeTotal) * 100 : 0}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(stage.color, 0.12),
              "& .MuiLinearProgress-bar": {
                bgcolor: stage.color,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useMuiTheme();

  const riskStats = useMemo(() => getRiskStats(), []);
  const sightingStats = useMemo(() => getSightingStats(), []);
  const woStats = useMemo(() => getWorkOrderStats(), []);
  const nodes = useMemo(() => generateNodes(), []);

  const nodeStats = useMemo(() => {
    const online = nodes.filter((n) => n.status === "online").length;
    const degraded = nodes.filter((n) => n.status === "degraded").length;
    const offline = nodes.filter((n) => n.status === "offline").length;
    return { total: nodes.length, online, degraded, offline };
  }, [nodes]);

  // Recent critical risks (last 24h)
  const recentCriticalRisks = useMemo(() => {
    return risksData
      .filter((r) => r.severity === "critical" && r.status !== "closed" && r.status !== "resolved")
      .slice(0, 5);
  }, []);

  // Recent work orders
  const recentWorkOrders = useMemo(() => {
    return [...workOrdersData]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, []);

  // Risk severity pie chart data
  const riskSeverityData = useMemo(
    () => [
      { id: 0, value: riskStats.bySeverity.critical, label: "Critical", color: theme.palette.error.main },
      { id: 1, value: riskStats.bySeverity.high, label: "High", color: theme.palette.warning.main },
      { id: 2, value: riskStats.bySeverity.medium, label: "Medium", color: theme.palette.warning.light },
      { id: 3, value: riskStats.bySeverity.low, label: "Low", color: theme.palette.success.main },
    ],
    [riskStats, theme]
  );

  // Sighting type bar chart data (top 6 types)
  const sightingTypeData = useMemo(() => {
    const typeLabels: Record<string, string> = {
      earth_moving: "Earth Moving",
      road_works: "Road Works",
      building_works: "Building",
      machinery_parked: "Machinery",
      agi_activity: "AGI",
      marker_posts: "Markers",
      forestry: "Forestry",
      quarry: "Quarry",
      flooding: "Flooding",
      ditching: "Ditching",
      network_works: "Network",
      fencing: "Fencing",
      other: "Other",
      unidentified: "Unidentified",
    };

    return Object.entries(sightingStats.byType)
      .map(([key, count]) => ({ type: typeLabels[key] || key, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [sightingStats]);

  // Status color helper
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getWoStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "error";
      case "dispatched":
        return "warning";
      case "tech_aware":
        return "info";
      case "on_site":
        return "info";
      case "completed":
        return "success";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getWoStatusLabel = (status: string) => {
    switch (status) {
      case "tech_aware":
        return "Tech Aware";
      case "on_site":
        return "On Site";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const activeRisks = riskStats.active + riskStats.investigating + riskStats.acknowledged;
  const activeWorkOrders = woStats.open + woStats.dispatched + woStats.techAware + woStats.onSite;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Summary Cards ────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <SummaryCard
          title="Risks & Alerts"
          total={riskStats.total}
          icon={<WarningIcon />}
          color={theme.palette.error.main}
          subtitle={`${activeRisks} active`}
          onClick={() => navigate(ROUTES.RISKS)}
          chips={[
            { label: `${riskStats.bySeverity.critical} Critical`, color: "error" },
            { label: `${riskStats.bySeverity.high} High`, color: "warning" },
          ]}
        />
        <SummaryCard
          title="Sightings"
          total={sightingStats.total}
          icon={<VisibilityIcon />}
          color={theme.palette.warning.main}
          subtitle={`${sightingStats.open} open`}
          onClick={() => navigate(ROUTES.SIGHTINGS)}
          chips={[
            { label: `${sightingStats.open} Open`, color: "error" },
            { label: `${sightingStats.dispatched} Dispatched`, color: "warning" },
          ]}
        />
        <SummaryCard
          title="Work Orders"
          total={woStats.total}
          icon={<AssignmentIcon />}
          color={theme.palette.info.main}
          subtitle={`${activeWorkOrders} in progress`}
          onClick={() => navigate(ROUTES.WORK_ORDERS)}
          chips={[
            ...(woStats.slaBreached > 0
              ? [{ label: `${woStats.slaBreached} SLA Breached`, color: "error" as const }]
              : []),
            { label: `${woStats.byPriority.critical} Critical`, color: "error" },
          ]}
        />
        <SummaryCard
          title="Network Nodes"
          total={nodeStats.total}
          icon={<RouterIcon />}
          color={theme.palette.success.main}
          subtitle={`${nodeStats.online} online`}
          onClick={() => navigate(ROUTES.GEOSPATIAL)}
          chips={[
            { label: `${nodeStats.online} Online`, color: "success" },
            ...(nodeStats.degraded > 0
              ? [{ label: `${nodeStats.degraded} Degraded`, color: "warning" as const }]
              : []),
            ...(nodeStats.offline > 0
              ? [{ label: `${nodeStats.offline} Offline`, color: "error" as const }]
              : []),
          ]}
        />
      </Box>

      {/* ── Charts Row ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3, alignItems: "stretch" }}>
        {/* Risk Severity Breakdown */}
        <Card variant="outlined" sx={{ flex: 1, minWidth: 300, display: "flex" }}>
          <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <SectionHeader
              title="Risk Severity Breakdown"
              action={{ label: "View All", onClick: () => navigate(ROUTES.RISKS) }}
            />
            <Box sx={{ flex: 1, minHeight: 250, mt: 1 }}>
              <PieChart
                series={[
                  {
                    data: riskSeverityData,
                    innerRadius: 40,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { fade: "global", highlight: "item" },
                  },
                ]}
                slotProps={{
                  legend: {
                    direction: "vertical",
                    position: { vertical: "middle", horizontal: "end" },
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Sightings by Type */}
        <Card variant="outlined" sx={{ flex: 1.5, minWidth: 400, display: "flex" }}>
          <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <SectionHeader
              title="Sightings by Type"
              action={{ label: "View All", onClick: () => navigate(ROUTES.SIGHTINGS) }}
            />
            <Box sx={{ flex: 1, minHeight: 250, mt: 1 }}>
              <BarChart
                xAxis={[
                  {
                    scaleType: "band",
                    data: sightingTypeData.map((d) => d.type),
                    tickLabelStyle: { fontSize: 11, angle: -30, textAnchor: "end" },
                  },
                ]}
                series={[
                  {
                    data: sightingTypeData.map((d) => d.count),
                    label: "Sightings",
                    color: theme.palette.primary.main,
                  },
                ]}
                margin={{ bottom: 60, left: 35, right: 10, top: 30 }}
                borderRadius={4}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ── Middle Row: Work Order Pipeline + Quick Stats ─────────────────── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {/* Work Order Pipeline */}
        <Card variant="outlined" sx={{ flex: 1, minWidth: 280 }}>
          <CardContent>
            <SectionHeader
              title="Work Order Pipeline"
              action={{ label: "View All", onClick: () => navigate(ROUTES.WORK_ORDERS) }}
            />
            <WorkOrderPipeline stats={woStats} />
            {woStats.slaBreached > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" alignItems="center" spacing={1}>
                  <WarningIcon fontSize="small" color="error" />
                  <Typography variant="body2" color="error.main" fontWeight={600}>
                    {woStats.slaBreached} SLA Breaches
                  </Typography>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        {/* Operational Summary */}
        <Card variant="outlined" sx={{ flex: 1, minWidth: 280 }}>
          <CardContent>
            <SectionHeader title="Operational Summary" />
            <Stack spacing={2}>
              {/* Risk Status Distribution */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Risk Status
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Chip label={`${riskStats.active} Active`} size="small" color="error" />
                  <Chip label={`${riskStats.investigating} Investigating`} size="small" color="warning" />
                  <Chip label={`${riskStats.acknowledged} Acknowledged`} size="small" color="info" variant="outlined" />
                  <Chip label={`${riskStats.resolved} Resolved`} size="small" color="success" variant="outlined" />
                </Stack>
              </Box>

              <Divider />

              {/* Sighting Sources */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Detection Sources
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {Object.entries(sightingStats.bySource).map(([source, count]) => (
                    <Chip
                      key={source}
                      label={`${count} ${source.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* Work Order Priority */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Work Order Priority
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Chip label={`${woStats.byPriority.critical} Critical`} size="small" color="error" />
                  <Chip label={`${woStats.byPriority.high} High`} size="small" color="warning" />
                  <Chip label={`${woStats.byPriority.medium} Medium`} size="small" color="info" variant="outlined" />
                  <Chip label={`${woStats.byPriority.low} Low`} size="small" color="success" variant="outlined" />
                </Stack>
              </Box>

              <Divider />

              {/* Network Health */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Network Health
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(nodeStats.online / nodeStats.total) * 100}
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {((nodeStats.online / nodeStats.total) * 100).toFixed(1)}%
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {nodeStats.online} of {nodeStats.total} nodes online
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* ── Tables Row ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {/* Critical Risks */}
        <Card variant="outlined" sx={{ flex: 1, minWidth: 400 }}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <SectionHeader
                title="Active Critical Risks"
                action={{ label: "View All", onClick: () => navigate(ROUTES.RISKS) }}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentCriticalRisks.map((risk) => (
                    <TableRow
                      key={risk.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/risks/${risk.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {risk.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {risk.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={risk.severity} size="small" color={getSeverityColor(risk.severity) as any} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={risk.status}
                          size="small"
                          color={getSeverityColor(risk.severity) as any}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                          {risk.location}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(risk.timestamp), { addSuffix: true })}
                        </Typography>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <IconButton size="small">
                          <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentCriticalRisks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Stack alignItems="center" spacing={1}>
                          <CheckCircleIcon color="success" />
                          <Typography variant="body2" color="text.secondary">
                            No active critical risks
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Recent Work Orders */}
        <Card variant="outlined" sx={{ flex: 1, minWidth: 400 }}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <SectionHeader
                title="Recent Work Orders"
                action={{ label: "View All", onClick: () => navigate(ROUTES.WORK_ORDERS) }}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Zone</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentWorkOrders.map((wo) => (
                    <TableRow
                      key={wo.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {wo.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {wo.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={wo.priority} size="small" color={getPriorityColor(wo.priority) as any} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getWoStatusLabel(wo.status)}
                          size="small"
                          color={getWoStatusColor(wo.status) as any}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{wo.zone}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(wo.createdAt), { addSuffix: true })}
                        </Typography>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <IconButton size="small">
                          <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

    </Box>
  );
};
